import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { CustomAxiosRequestConfig, RequestOptions, ResState } from "../type";
import Gateway from "../gateway";
import { replaceBaseUrlPlugin } from "../gateway/plugins/request/replaceBaseUrlPlugin";
import replaceDemoTradingUrlPlugin from "../gateway/plugins/request/replaceDemoTradingUrlPlugin";
import tokenHeaderPlugin from "../gateway/plugins/request/tokenHeaderPlugin";
import fingerprintPlugin from "../gateway/plugins/request/fingerprintPlugin";
import setResponseTokenPlugin from "../gateway/plugins/response/setResponseTokenPlugin";
import removeGlobalAccountTypePlugin from "../gateway/plugins/response/removeGlobalAccountTypePlugin";

const baseGateway = new Gateway();

baseGateway.use(replaceBaseUrlPlugin);
baseGateway.use(replaceDemoTradingUrlPlugin);
baseGateway.use(tokenHeaderPlugin);
baseGateway.use(fingerprintPlugin);
baseGateway.use(setResponseTokenPlugin);
baseGateway.use(removeGlobalAccountTypePlugin);

class Request {
  private instance: AxiosInstance;

  private omitQuerySerialization?: boolean;

  constructor(options: RequestOptions) {
    const { config, gateway, omitQuerySerialization } = options;

    this.omitQuerySerialization = omitQuerySerialization;

    this.instance = axios.create(config);

    this.instance.interceptors.request.use(
      (config: CustomAxiosRequestConfig) => {
        config = baseGateway.applyRequestAll(config, options);
        if (gateway) {
          config = gateway.applyRequestAll(config, options);
        }

        return config;
      },
      (error) => {
        error = baseGateway.applyRequestErrorAll(error, options);
        if (gateway) {
          error = gateway.applyRequestErrorAll(error, options);
        }
        return Promise.reject(error);
      },
    );

    this.instance.interceptors.response.use(
      (res: AxiosResponse) => {
        res = baseGateway.applyResponseAll(res, options);
        if (gateway) {
          res = gateway.applyResponseAll(res, options);
        }
        return res.data;
      },
      (error: AxiosError) => {
        error = baseGateway.applyResponseErrorAll(error, options);
        if (gateway) {
          error = gateway.applyResponseErrorAll(error, options);
        }
        return Promise.reject(error?.message);
      },
    );
  }

  public request<T = any>(config: AxiosRequestConfig): Promise<ResState<T>> {
    return this.instance.request(config);
  }

  public get<T>(
    url: string,
    params?: object,
    config?: object,
  ): Promise<ResState<T>> {
    return this.instance.get(
      url,
      this.omitQuerySerialization
        ? { params, ...config }
        : { ...params, ...config },
    );
  }

  public post<T>(
    url: string,
    params?: object,
    config?: object,
  ): Promise<ResState<T>> {
    return this.instance.post(url, params, config);
  }
}

export default Request;
